import { SphereIconButton } from "@components/common/sphere-icon-button";
import { CloseCancelErrorIcon, FileDocumentIcon, FontWeights, LinearProgress, Text } from "@flat-ui";
import { BackgroundTaskState } from "@faro-lotv/service-wires";
import { useCancelUpload } from "@hooks/upload-tasks/use-cancel-upload";
import { Box, Stack, useTheme } from "@mui-wrapper";
import { useAppDispatch } from "@store/store-helper";
import { removeOne } from "@store/upload-tasks/upload-tasks-slice";
import { DEFAULT_TEXT_FONT_SIZE, withEllipsis } from "@styles/common-styles";
/** The class of the cancel button */
const cancelButtonClass = "cancelButton";

/** The class of the progress bar */
const progressBarClass = "progressBar";

/** The class of the file name */
const fileNameClass = "fileName";

/** Renders a markup attachment upload task */
export function MarkupUploadingAttachmentItem({
  task,
  isLastElement
}) {
  const theme = useTheme();
  const cancelUploadTask = useCancelUpload();
  const dispatch = useAppDispatch();
  async function cancelUpload() {
    await cancelUploadTask(task.id);
    if (task.status === BackgroundTaskState.failed) {
      dispatch(removeOne(task.id));
    }
  }
  return <Box data-testid="markup-side-panel-attachment-item-container" sx={{
    display: "flex",
    gap: "8px",
    borderBottom: !isLastElement ? 1 : 0,
    borderColor: "divider",
    padding: "8px",
    transition: "background-color 0.3s",
    alignItems: "center",
    "&:hover": {
      backgroundColor: "surfaces.tertiary",
      cursor: "pointer",
      [`& .${fileNameClass}`]: {
        color: "primary.main"
      },
      [`& .${cancelButtonClass}`]: {
        display: "block"
      },
      [`& .${progressBarClass}`]: {
        display: "none"
      }
    }
  }}>
      {/* File Icon */}
      <Box sx={{
      height: "43px",
      display: "flex",
      alignItems: "center"
    }}>
        <FileDocumentIcon sx={{
        width: "60px",
        height: "32px",
        color: "text.secondary"
      }} />
      </Box>

      <Stack sx={{
      gap: 0.5,
      width: "calc(100% - 110px)"
    }}>
        {/* File name */}
        <Text variant="bodyS" className={fileNameClass} sx={{
        color: "text.primary",
        ...withEllipsis
      }}>
          <var>{task.fileName}</var>
        </Text>

        {/* Failed message or progress bar */}
        {task.status === BackgroundTaskState.failed ? <Text variant="bodyXS" sx={{
        color: "error.main",
        fontWeight: FontWeights.SemiBold,
        ...withEllipsis
      }}>
            {task.errorMessage ?? "Unexpected error. Please contact support."}
          </Text> : <LinearProgress value={task.progress} variant={task.progress > 0 ? "determinate" : "indeterminate"} />}
      </Stack>

      {/* Cancel icon */}
      <Box sx={{
      textAlign: "center",
      width: "50px"
    }}>
        {task.progress > 0 && task.status !== BackgroundTaskState.failed && <var className={progressBarClass} style={{
        fontSize: DEFAULT_TEXT_FONT_SIZE,
        color: theme.vars.palette.text.secondary
      }}>
            {/* Round because CoreFileUploader sends "progress: 0.00001" when
                upload starts, to switch to determinate progress bar. */}
            {Math.round(task.progress)}%
          </var>}

        <Box className={cancelButtonClass} sx={{
        display: task.status === BackgroundTaskState.failed ? "block" : "none"
      }}>
          <SphereIconButton component={CloseCancelErrorIcon} onClick={cancelUpload} iconSize="18px" />
        </Box>
      </Box>
    </Box>;
}